import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { cloneDeep } from 'lodash';
import PropTypes from 'prop-types';
import { nanoid } from 'nanoid';

import {
  DEFAULT_GROUP_NAME,
  ERROR_SAVE_DRAFT,
  readInstantSurvey,
  RESPONSE_COUNT_INCREMENT,
  SURVEY_GROUP_VALIDATION_STATUS,
  SURVEY_TYPES,
  validateInstantSurvey,
  validateInstantSurveyPrompts,
  VIDEO_RESPONSE_COUNT_INCREMENT,
} from '@api/instant_survey';

import { ActionArea, Button, CheckCircleIcon, OverlayTriggerTooltip, toast } from '@utilities';
import { PlusIcon } from '@utilities/icons';

import { CONCEPT_ERROR_STATE, CONCEPT_ERROR_MESSAGES } from './utilities/constants';
import ModalDeleteQuotaGroup from './components/ModalDeleteQuotaGroup';
import ModalDuplicateGroup from './components/ModalDuplicateGroup';
import ModalMissingRequiredPrompts from './components/ModalMissingRequiredPrompts';
import ModalValidationErrors from './components/ModalValidationErrors';
import QuotaGroupCard from './components/QuotaGroupCard';
import SurveyToolbar from '../SurveyToolbar';

import { SurveyContext } from '../../utilities/context';

import styles from './_index.module.scss';

const Panelists = ({ defaultResponseCount, onNext }) => {
  const {
    activeGroups,
    insightsDocumentType,
    instantSurveyType,
    isResearchObjectivesTooLong,
    saveInstantSurvey,
    survey,
    updateSurvey,
  } = useContext(SurveyContext);

  const [groupToDelete, setGroupToDelete] = useState(null);
  const [groupToDuplicate, setGroupToDuplicate] = useState(null);
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [isValidatingPanelists, setIsValidatingPanelists] = useState(false);
  const [isValidatingResponses, setIsValidatingResponses] = useState(false);
  const [validatingSurveyId, setValidatingSurveyId] = useState(null);
  const [validationErrors, setValidationErrors] = useState(null);
  const [visiblePromptsByGroup, setVisiblePromptsByGroup] = useState(new Map());

  const { NOT_VALIDATED, VALIDATING } = SURVEY_GROUP_VALIDATION_STATUS;

  const surveysResponsesValidation = useSelector((state) => state?.websocket?.surveyValidation);

  const isMonadicConceptTest = instantSurveyType?.id === SURVEY_TYPES.MONADIC;
  const isVideoSurvey = instantSurveyType?.id === SURVEY_TYPES.VIDEO;

  const controller = new AbortController();

  const responseCountIncrement = isVideoSurvey
    ? VIDEO_RESPONSE_COUNT_INCREMENT
    : RESPONSE_COUNT_INCREMENT;
  const signal = controller.signal;
  const surveyIdRef = useRef(null);

  const updateVisiblePrompts = (groupId, updater) => {
    setVisiblePromptsByGroup((prev) => {
      const newVisiblePrompts = new Map(prev);
      const prevVisible = newVisiblePrompts.get(groupId) || new Set();
      const updatedVisible = updater(prevVisible);

      if (
        prevVisible.size === updatedVisible.size &&
        [...prevVisible].every((id) => updatedVisible.has(id))
      ) {
        return prev;
      }

      newVisiblePrompts.set(groupId, updatedVisible);
      return newVisiblePrompts;
    });
  };

  const currentTotalResponses = activeGroups.reduce((acc, group) => acc + group.quotaLimit, 0);
  const minimumResponsesRequired = Math.min(
    ...insightsDocumentType.samplePricing
      .filter(({ quotaGroups }) => quotaGroups === activeGroups.length)
      .map(({ responses }) => responses)
  );

  const canAddQuotaGroup = insightsDocumentType.samplePricing.find(
    ({ quotaGroups }) => quotaGroups === activeGroups.length + 1
  );
  const canDecrementResponses = insightsDocumentType.samplePricing.some(
    ({ responses }) => responses === currentTotalResponses - responseCountIncrement
  );
  const canIncrementResponses =
    currentTotalResponses <
    Math.max(
      ...insightsDocumentType.samplePricing
        .filter(({ quotaGroups }) => quotaGroups === activeGroups.length)
        .map(({ responses }) => responses)
    );

  const conceptErrorState = useMemo(() => {
    if (!isMonadicConceptTest) return null;

    const groupStates = activeGroups.map(({ conceptImage, conceptName }) => ({
      hasImage: Boolean(conceptImage),
      hasName: Boolean(conceptName),
    }));

    const someMissingBoth = groupStates.some(({ hasImage, hasName }) => !hasImage && !hasName);
    const someHaveImage = groupStates.some(({ hasImage }) => hasImage);
    const allHaveImage = groupStates.every(({ hasImage }) => hasImage);
    const someHaveName = groupStates.some(({ hasName }) => hasName);
    const allHaveName = groupStates.every(({ hasName }) => hasName);

    if (someMissingBoth) return CONCEPT_ERROR_STATE.MISSING_EITHER;
    if (someHaveImage && !allHaveImage) return CONCEPT_ERROR_STATE.INCONSISTENT_IMAGE;
    if (someHaveName && !allHaveName) return CONCEPT_ERROR_STATE.INCONSISTENT_NAME;

    return null;
  }, [isMonadicConceptTest, activeGroups]);

  const conceptErrorMessage = conceptErrorState ? CONCEPT_ERROR_MESSAGES[conceptErrorState] : null;

  const groupsMissingRequiredPrompts = activeGroups
    .map(({ id, name, promptAnswers }) => ({
      id,
      name,
      prompts: instantSurveyType.prompts.filter((prompt) => {
        const visiblePromptIds = visiblePromptsByGroup.get(id) || new Set();
        if (!visiblePromptIds.has(prompt.id)) return false;
        if (!prompt.isRequired) return false;

        const answers = promptAnswers.filter(
          ({ _delete, promptId }) => !_delete && prompt.id === promptId
        );
        return answers.length === 0 || answers.some(({ values }) => values.length === 0);
      }),
    }))
    .filter(({ prompts }) => prompts.length > 0);
  const remainingAvailableResponses =
    Math.max(
      ...insightsDocumentType.samplePricing
        .filter(({ quotaGroups }) => quotaGroups === activeGroups.length + 1)
        .map(({ responses }) => responses)
    ) - currentTotalResponses;

  const addQuotaGroupTooltip =
    canAddQuotaGroup && canIncrementResponses
      ? 'Add Quota Group'
      : !canAddQuotaGroup
        ? 'Maximum amount of quota groups reached'
        : 'Maximum amount of responses reached: you cannot create another quota group';
  const isContinueDisabled =
    currentTotalResponses < minimumResponsesRequired ||
    activeGroups.some(({ name }) => !name.trim()) ||
    new Set(activeGroups.map(({ name }) => name.trim())).size !== activeGroups.length ||
    isResearchObjectivesTooLong;
  const navigate = useNavigate();

  const onAddQuotaGroup = () => {
    updateSurvey({
      groups: [
        ...survey.groups,
        {
          genRepSampleType: null,
          id: nanoid(),
          name: `${DEFAULT_GROUP_NAME} (${activeGroups.length + 1})`,
          promptAnswers: [],
          quotaLimit: Math.min(defaultResponseCount, remainingAvailableResponses),
        },
      ],
    });
  };

  const saveAndValidateSurveyDraft = async () => {
    if (groupsMissingRequiredPrompts.length > 0 || conceptErrorMessage) {
      setIsValidationModalOpen(true);
      return null;
    }

    const survey = await handleSaveSurvey();
    if (!survey) return null;

    const isValid = await handleValidateSurveyPrompts(survey.id);
    return isValid ? survey : null;
  };

  const handleSaveSurvey = async () => {
    try {
      return await saveInstantSurvey();
    } catch (error) {
      console.error('Error saving survey draft:', error);
      toast(ERROR_SAVE_DRAFT, { status: 'error' });
      return null;
    }
  };

  const handleValidateSurveyPrompts = async (id) => {
    try {
      await validateInstantSurveyPrompts({ id });
      return true;
    } catch (error) {
      const validationErrors = error?.response?.data?.error || [];
      setValidationErrors(validationErrors);
      return false;
    }
  };

  const onContinue = async () => {
    setIsValidatingPanelists(true);

    try {
      const survey = await saveAndValidateSurveyDraft();
      if (survey) {
        onNext();
      }
    } catch (error) {
      console.error('Unexpected error during survey continuation:', error);
    } finally {
      setIsValidatingPanelists(false);
    }
  };

  const onDelete = (group) => {
    if (typeof group.id === 'string') {
      return updateSurvey({ groups: survey.groups.filter(({ id }) => id !== group.id) });
    }

    const affectedQuestions = [];
    for (const [questionId, logicRow] of Object.entries(survey.questionLogic)) {
      if (logicRow.skip?.logic) {
        for (const logic of logicRow.skip.logic) {
          if (logic.url_variable && logic.values.includes(String(group.id))) {
            const question = survey.questions.find(({ id }) => id === parseInt(questionId));
            affectedQuestions.push({
              id: question.id,
              text: question.text,
            });
            break;
          }
        }
      }
    }

    if (affectedQuestions.length > 0) {
      setGroupToDelete({
        affectedQuestions: affectedQuestions,
        id: group.id,
        name: group.name,
      });
    } else {
      onUpdateGroup(group.id, { _delete: true });
    }
  };

  const onDuplicateGroup = ({ genRepSampleType, name, promptAnswers, quotaLimit }) => {
    setGroupToDuplicate({
      genRepSampleType,
      id: nanoid(), // strip id from group
      name,
      promptAnswers: cloneDeep(promptAnswers),
      quotaLimit,
    });
  };

  const onUpdateGroup = (groupId, propsToUpdate) => {
    const groupIndex = survey.groups.findIndex(({ id }) => id === groupId);
    let updatedGroups = [...survey.groups];
    if (propsToUpdate.answers) propsToUpdate.validationStatus = null;
    updatedGroups[groupIndex] = Object.assign(updatedGroups[groupIndex], propsToUpdate);
    updateSurvey({ groups: updatedGroups });
  };

  const onValidateResponses = async () => {
    setIsValidatingResponses(true);

    try {
      const survey = await saveAndValidateSurveyDraft();
      if (!survey) return setIsValidatingResponses(false);

      const hasGroupsToValidate = survey.groups?.some(({ validationStatus }) =>
        [NOT_VALIDATED, VALIDATING].includes(validationStatus)
      );

      if (!hasGroupsToValidate) return setIsValidatingResponses(false);

      await validateInstantSurvey({ id: survey.id, signal });

      setValidatingSurveyId(survey.id);
      surveyIdRef.current = survey.id;
    } catch (error) {
      console.error('Error validating responses:', error);
      toast('Validation failed', { status: 'error' });
      setIsValidatingResponses(false);
    }
  };

  useEffect(() => {
    if (!validatingSurveyId) return;

    const surveyValidation = surveysResponsesValidation?.find(
      ({ id }) => id === surveyIdRef.current
    );

    const validStatuses = ['COMPLETED', 'FAILED'];

    if (!validStatuses.includes(surveyValidation?.status)) return;

    const fetchSurveyValidation = async () => {
      try {
        const { groups } = await readInstantSurvey({ id: surveyIdRef.current });

        updateSurvey({ groups });
      } catch (error) {
        console.error('Error reading survey:', error);
      } finally {
        setValidatingSurveyId(null);
        surveyIdRef.current = null;
        setIsValidatingResponses(false);
      }
    };

    fetchSurveyValidation();
  }, [surveysResponsesValidation]);

  return (
    <div className={styles['panelists']}>
      <ActionArea
        buttons={[
          <Button
            data-testid="cancel-button"
            key="cancel-button"
            onClick={() => navigate(-1)}
            text="Cancel"
            variant="secondary"
          />,
          <Button
            data-testid="continue-button"
            isDisabled={isContinueDisabled}
            isLoading={isValidatingPanelists}
            key="continue-button"
            onClick={onContinue}
            text="Continue"
          />,
        ]}
        className={styles['panelists-layout']}
      >
        <SurveyToolbar
          isSaveAsDraftDisabled={isContinueDisabled}
          tools={[
            <OverlayTriggerTooltip
              content={isValidatingResponses ? 'Validating Sample...' : 'Validate Sample'}
            >
              <button disabled={isValidatingResponses} onClick={onValidateResponses}>
                <CheckCircleIcon />
              </button>
            </OverlayTriggerTooltip>,
            <OverlayTriggerTooltip content={addQuotaGroupTooltip}>
              <button
                disabled={!canAddQuotaGroup || !canIncrementResponses}
                onClick={onAddQuotaGroup}
              >
                <PlusIcon />
              </button>
            </OverlayTriggerTooltip>,
          ]}
        />
        {currentTotalResponses < minimumResponsesRequired && (
          <div className={styles['panelists-pricing-error']}>
            An Instant Survey must have a minimum of {minimumResponsesRequired} responses.
          </div>
        )}
        <ul className={styles['panelists-cards']}>
          {activeGroups.map((group) => (
            <li key={group.id}>
              <QuotaGroupCard
                canDecrementResponses={canDecrementResponses}
                canDelete={activeGroups.length > 1}
                canDuplicate={canAddQuotaGroup && canIncrementResponses}
                canIncrementResponses={canIncrementResponses}
                group={group}
                groupToDelete={groupToDelete}
                isValidatingResponses={isValidatingResponses}
                isVideoSurvey={isVideoSurvey}
                onDelete={() => onDelete(group)}
                onDuplicate={() => onDuplicateGroup(group)}
                onUpdate={(propsToUpdate) => onUpdateGroup(group.id, propsToUpdate)}
                survey={survey}
                surveyType={instantSurveyType}
                updateVisiblePrompts={updateVisiblePrompts}
              />
            </li>
          ))}
        </ul>
        <div className={styles['panelists-add-quota-group']}>
          <OverlayTriggerTooltip content={addQuotaGroupTooltip} position="bottom">
            <button
              data-testid="add-quota-group-button"
              disabled={!canAddQuotaGroup || !canIncrementResponses}
              onClick={onAddQuotaGroup}
            >
              <PlusIcon />
              <span>
                Add another <strong>Quota Group</strong>
              </span>
            </button>
          </OverlayTriggerTooltip>
        </div>
      </ActionArea>
      {groupToDelete && (
        <ModalDeleteQuotaGroup
          group={groupToDelete}
          onClose={() => setGroupToDelete(null)}
          onSubmit={() => {
            onUpdateGroup(groupToDelete.id, { _delete: true });
            setGroupToDelete(null);
          }}
        />
      )}
      {groupToDuplicate && (
        <ModalDuplicateGroup
          group={groupToDuplicate}
          groups={activeGroups}
          onClose={() => setGroupToDuplicate(null)}
          onSubmit={(group) => {
            updateSurvey({ groups: [...survey.groups, group] });
            setGroupToDuplicate(null);
          }}
          remainingAvailableResponses={remainingAvailableResponses}
        />
      )}
      {isValidationModalOpen && (
        <ModalMissingRequiredPrompts
          conceptErrorMessage={conceptErrorMessage}
          groups={activeGroups}
          invalidGroups={groupsMissingRequiredPrompts}
          onClose={() => setIsValidationModalOpen(false)}
        />
      )}
      {validationErrors && (
        <ModalValidationErrors
          onClose={() => setValidationErrors(null)}
          survey={survey}
          validationErrors={validationErrors}
        />
      )}
    </div>
  );
};

Panelists.propTypes = {
  defaultResponseCount: PropTypes.number.isRequired,
  onNext: PropTypes.func.isRequired,
};

export default Panelists;
